import React, { useEffect, useState, type ComponentType } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import moment from 'moment-timezone';
import i18n from '../../i18n';
import { TopSectionContainer } from '../../components/this_week.component';
import { loadFederatedModule } from '../../utils/loadFederatedModule';
import { ErrorBoundary } from '../project-management/ErrorBoundary/ErrorBoundary';
import { initialPreferences, savePreferences } from './preferences';

// Shape of the workstreams remote's MyWorkCalendar (WorkstreamsUI/MyWorkCalendar).
type MyWorkCalendarRemoteProps = {
  anchorDate: string;
  baseUrl?: string;
  currentUserId: number;
  analytics?: { trackEvent?: (name: string, details?: Record<string, unknown>) => void };
  onActivityClick?: (activityId: number) => void;
  onMeetingClick?: (meetingExternalId: string) => void;
  initialPreferences?: Record<string, unknown>;
  onPreferencesChange?: (prefs: unknown) => void;
  refreshKey?: number;
  onChange?: () => void;
  statusEditable?: boolean;
  profileTimezone?: boolean;
};

type ThisWeekData = { disablePersonalOkr?: boolean };

let roots: Root[] = [];
let refreshCalendar: (() => void) | undefined;

const unmount = () => {
  roots.forEach((root) => root.unmount());
  roots = [];
  refreshCalendar = undefined;
};

/** Runs after commit, off the render stack, so it may unmount the root it sits in. */
const Defer = ({ run }: { run: () => void }) => {
  useEffect(() => {
    const timer = setTimeout(run);
    return () => clearTimeout(timer);
  }, [run]);
  return null;
};

const Calendar = ({
  Remote,
  userId,
}: {
  Remote: ComponentType<MyWorkCalendarRemoteProps>;
  userId: number;
}) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [preferences] = useState(() => initialPreferences(userId));

  // Edits and deletes already refresh through backendLoadAfterSave, so closing needn't.
  const openActivity = (id: number) => window.openFullPopupV1(String(id), () => undefined);
  const openMeeting = (id: string) => {
    window.open(`${window.wbConfig.baseUrl}/meeting/meetingDetails?id=${encodeURIComponent(id)}`, '_blank');
  };

  useEffect(() => {
    const refreshFromPage = () => setRefreshKey((key) => key + 1);
    refreshCalendar = refreshFromPage;
    return () => {
      if (refreshCalendar === refreshFromPage) refreshCalendar = undefined;
    };
  }, []);

  // Three 240px day-column minimums. Grid, so the calendar's `height: 100%` resolves against it.
  return (
    <div data-testid="my-work-calendar-container" style={{ display: 'grid', minHeight: 720 }}>
      <Remote
        anchorDate={moment().format('YYYY-MM-DD')}
        baseUrl={window.location.origin}
        currentUserId={userId}
        analytics={{ trackEvent: window.wb_track }}
        onActivityClick={openActivity}
        onMeetingClick={openMeeting}
        initialPreferences={preferences}
        onPreferencesChange={(prefs) => savePreferences(userId, prefs)}
        refreshKey={refreshKey}
        onChange={() => myworkCount()}
        // Opt-ins the agent's This Week artifact leaves off.
        statusEditable
        profileTimezone
      />
    </div>
  );
};

/** Legacy tray on top, federated My Work Calendar below; legacy This Week if the remote fails. */
window.initMyWorkCalendar = (data: ThisWeekData) => {
  unmount();
  const trayEl = document.getElementById('this-week-tray');
  const calendarEl = document.getElementById('my-work-calendar');
  if (!trayEl || !calendarEl) return;
  // Fired by the My Work menu on tab switch; off() first so re-inits don't stack handlers.
  $(document).off('unmount_this_week_app_comp', unmount).on('unmount_this_week_app_comp', unmount);

  const tray = createRoot(trayEl);
  const calendar = createRoot(calendarEl);
  roots = [tray, calendar];
  // Legacy renders its own tray, so both roots go before it mounts.
  const fallBack = () => {
    if (!roots.includes(calendar)) return;
    unmount();
    // The PHP view drops legacy's 800px floor for the calendar; legacy needs it back.
    document.getElementById('thisWeekAIs')?.style.removeProperty('min-height');
    window.initThisWeekAppComp(data);
  };

  const userId = Number((window as any).uData?.user_id);
  // Preferences are keyed by user, so without one the calendar can't run.
  if (!userId) {
    fallBack();
    return;
  }

  tray.render(
    <I18nextProvider i18n={i18n}>
      <TopSectionContainer disableFeature={data.disablePersonalOkr} />
    </I18nextProvider>,
  );

  type RemoteModule = { default: ComponentType<MyWorkCalendarRemoteProps> };
  loadFederatedModule<RemoteModule>('WorkstreamsUI/MyWorkCalendar')
    .then(({ default: Remote }) => {
      if (!roots.includes(calendar)) return;
      calendar.render(
        <ErrorBoundary renderError={() => <Defer run={fallBack} />}>
          <Calendar Remote={Remote} userId={userId} />
        </ErrorBoundary>,
      );
    })
    .catch(fallBack);
};

// Edits made elsewhere on the page (full popup, global Quick Add); no-op when not mounted.
window.refreshMyWorkCalendar = () => refreshCalendar?.();
